<template>
  <div
    class="flex flex-col items-start px-[16px] pt-[16px] pb-[24px] bg-white rounded-[12px] shadow-[0_2px_12px_0_rgba(104,104,104,0.2)] gap-[24px]"
  >
    <SectionWithChoice
      v-if="strengthSectionProps.choices.length > 1"
      v-bind="strengthSectionProps"
      @item-selected="handleStrengthSelected"
    >
      <template v-if="strengthModal" v-slot:modal-content>
        <div
          v-for="(item, idx) in strengthModal?.items"
          :key="idx"
          class="py-[16px] border-0 border-t-[1px] border-solid border-[#7AA9E9]"
        >
          <div class="font-bold text-[16px] text-[#003F82] leading-[140%]" v-html="item.title"></div>
          <div
            class="font-normal text-[16px] text-[#003F82] leading-[140%] item-description-markup"
            v-html="item.descriptionMarkup"
          ></div>
        </div>
      </template>
    </SectionWithChoice>
    <SectionWithChoice
      v-if="genericBrandedSectionProps"
      v-bind="genericBrandedSectionProps"
      @item-selected="handleProductSelected"
    >
      <template v-slot:modal-content>
        <div class="font-normal text-[16px] text-[#003F82] leading-[140%]">
          Generic medicines contain the same active ingredient as branded ones, so they're equally effective, but
          they're available at a lower price because you're not paying for a premium brand name. Generic manufacturers
          have to prove their products are medically equivalent to the branded versions — i.e. they offer the same
          quality, strength, stability and effectiveness.
        </div>
      </template>
    </SectionWithChoice>
    <SectionWithChoice
      v-if="repeatOrderSectionProps"
      v-bind="repeatOrderSectionProps"
      @item-selected="handleRepeatOrderSelected"
    >
      <template v-slot:modal-content>
        <div class="py-[16px] border-0 border-t-[1px] border-solid border-[#7AA9E9]">
          <div class="font-bold text-[16px] text-[#003F82] leading-[140%]">Cancel anytime</div>
          <div class="font-normal text-[16px] text-[#003F82] leading-[140%] item-description-markup">
            If you find that your subscription isn't right for you, you can easily cancel whenever you need. As long as
            you cancel at least 48 hours before your next delivery, there are no restrictions.
          </div>
        </div>
        <div class="py-[16px] border-0 border-t-[1px] border-solid border-[#7AA9E9]">
          <div class="font-bold text-[16px] text-[#003F82] leading-[140%]">Automatic reordering</div>
          <div class="font-normal text-[16px] text-[#003F82] leading-[140%] item-description-markup">
            You don't have to worry about running out of medication or placing a new order every time you need a refill.
            We take care of that for you.
          </div>
        </div>
      </template>
    </SectionWithChoice>
    <Dropdown
      v-if="isSubscriptionSelected"
      v-bind="deliveryScheduleDropdownProps"
      @option-selected="handleDeliveryScheduleSelection"
    />
    <Dropdown v-bind="quantityDropdownProps" @option-selected="handleQuantitySelection" />
    <PriceBreakdown v-bind="priceProps" />
    <CtaLink v-bind="ctaLinkProps"> Get started for free </CtaLink>
    <LabelTag v-if="discountTag" color="green">
      <span v-if="discountTag.renderTick" class="appearence-none"> &#10003; </span>

      {{ discountTag.discountText }}
    </LabelTag>
  </div>
</template>

<script setup lang="ts">
import type { Ref } from "vue";
import { computed, ref, watch } from "vue";
import SectionWithChoice from "@/ProductPage/components/SectionWithChoice";
import Dropdown from "@/ProductPage/components/Dropdown";
import PriceBreakdown from "@/ProductPage/components/PriceBreakdown";
import CtaLink from "@/ProductPage/components/CtaLink";
import LabelTag, { LabelTagColor } from "@/ProductPage/components/LabelTag";
import * as Schema from "@/ProductPage/ProductPageStateSchema";
import { Quantity } from "@/ProductPage/ProductPageStateSchema";
import { addUserProperties, amplitudeEvent } from "@/MXPixel/MedexpressAmplitude";
import { getAnalyticsInfo } from "@/ProductPage/Analytics";
import { StrengthInformationModal } from "./";
import { flatten } from "lodash";

interface State {
  selectedProductCode: string;
  selectedStrengthCode: string;
  selectedQuantityFrequencyCombination: {
    selectedQuantityCode: string;
    selectedDeliverySchedule?: string;
  };
  selectedRepeatOrder: string;
}

const props = defineProps<{
  data: Schema.Products;
  strengthModal?: StrengthInformationModal;
}>();

const state: Ref<State> = ref(getInitialState());

function getInitialState(): State {
  const initialSelectedProduct = checkNonNullOrThrow(
    props.data.products.get(props.data.currentProduct),
    "Selected product must be defined"
  );
  const initialSelectedStrength = checkNonNullOrThrow(
    initialSelectedProduct.strengths.get(initialSelectedProduct.currentStrength),
    "Selected strength must be defined"
  );
  const initialSelectedQuantity = checkNonNullOrThrow(
    initialSelectedStrength.quantities.get(initialSelectedStrength.currentOTPQuantity),
    "Selected quantity must be defined"
  );

  return {
    selectedProductCode: initialSelectedProduct.code,
    selectedStrengthCode: initialSelectedStrength.code,
    selectedQuantityFrequencyCombination: {
      selectedQuantityCode: initialSelectedQuantity.code,
      selectedDeliverySchedule: undefined,
    },
    selectedRepeatOrder: initialSelectedStrength.quantities.size > 0 ? "OTP" : "SUBSCRIPTION",
  };
}

const selectedProduct = computed(() => {
  return checkNonNullOrThrow(
    props.data.products.get(state.value.selectedProductCode),
    "Selected product must be defined"
  );
});

const selectedStrength = computed(() => {
  return checkNonNullOrThrow(
    selectedProduct.value.strengths.get(state.value.selectedStrengthCode),
    "Selected strength must be defined"
  );
});

const selectedQuantity = computed(() => {
  return checkNonNullOrThrow(
    getAvailableQuantities(state.value.selectedQuantityFrequencyCombination.selectedDeliverySchedule).get(
      state.value.selectedQuantityFrequencyCombination.selectedQuantityCode
    ),
    "Selected quantity must be defined"
  );
});

const isSubscriptionSelected = computed(() => {
  return state.value.selectedRepeatOrder === "SUBSCRIPTION";
});

const strengthSectionProps = computed(() => {
  if (selectedProduct.value.strengths.size >= 1) {
    const choices = Array.from(selectedProduct.value.strengths, ([strengthCode, strength]) => ({
      strengthCode,
      strength,
    })).map(({ strengthCode, strength }) => {
      return {
        id: strengthCode,
        text: strength.label,
        description: strength.description,
        selected: strengthCode === state.value.selectedStrengthCode,
        disabled: !strength.inStock,
        ...(strength.tag && {
          tagInformation: {
            color: strength.tag.type,
            text: strength.tag.text,
          },
        }),
      };
    });

    return {
      headerText: "Strength",
      choices: choices,
      ...(props.strengthModal && {
        modalInformation: {
          title: props.strengthModal.title,
          visible: false,
        },
      }),
    };
  }

  return undefined;
});

const genericBrandedSectionProps = computed(() => {
  if (props.data.products.size > 1) {
    const choices = Array.from(props.data.products, ([productCode, product]) => ({ productCode, product })).map(
      ({ productCode, product }) => {
        return {
          id: productCode,
          text: product.label,
          description: product.description,
          selected: productCode === state.value.selectedProductCode,
          disabled: !product.inStock,
          ...(product.tag && {
            tagInformation: {
              color: product.tag.type,
              text: product.tag.text,
            },
          }),
        };
      }
    );

    return {
      headerText: "Generic or branded",
      choices: choices,
      modalInformation: {
        title: "Generic vs. branded",
        visible: false,
      },
    };
  }

  return undefined;
});

const repeatOrderSectionProps = computed(() => {
  const canOfferSubscription = Array.from(selectedStrength.value.subscriptionFrequencies.values()).length > 0;
  const canOfferOTP = Array.from(selectedStrength.value.quantities.values()).length > 0;

  if (canOfferSubscription) {
    const otpChoice = {
      id: "OTP",
      text: "One-time purchase",
      selected: state.value.selectedRepeatOrder === "OTP",
      disabled: false,
    };

    const discountTag = flatten(
      Array.from(selectedStrength.value.subscriptionFrequencies.values()).map((subscriptionOption) =>
        Array.from(subscriptionOption.quantities.values())
      )
    )
      .map((subscriptionQuantity) =>
        subscriptionQuantity.discount ? subscriptionQuantity.discount.discountSaving : undefined
      )
      .reduce((accumulator, currentValue) => (accumulator === currentValue ? accumulator : undefined));

    const subscriptionChoice = {
      id: "SUBSCRIPTION",
      text: "Subscription",
      selected: state.value.selectedRepeatOrder === "SUBSCRIPTION",
      disabled: false,
      ...(discountTag && {
        tagInformation: {
          color: "green",
          text: `${discountTag} SAVING`,
        },
      }),
    };

    const choices = [canOfferOTP && otpChoice, subscriptionChoice];

    return {
      headerText: "Repeat orders",
      choices: choices,
      modalInformation: {
        title: "Why we recommend a subscription",
        visible: false,
      },
    };
  }

  return undefined;
});

const deliveryScheduleDropdownProps = computed(() => {
  const deliveryScheduleOptions = Array.from(selectedStrength.value.subscriptionFrequencies.values()).map(
    (subscriptionOption) => ({
      id: subscriptionOption.code,
      text: `${subscriptionOption.label}`,
    })
  );

  return {
    label: "Delivery schedule",
    options: deliveryScheduleOptions,
    default: state.value.selectedQuantityFrequencyCombination.selectedDeliverySchedule,
  };
});

const quantityDropdownProps = computed(() => {
  const quantityOptions = Array.from(
    getAvailableQuantities(state.value.selectedQuantityFrequencyCombination.selectedDeliverySchedule),
    ([quantityCode, quantity]) => ({
      quantityCode,
      quantity,
    })
  ).map(({ quantityCode, quantity }) => {
    return {
      id: quantityCode,
      text: quantity.label,
    };
  });

  return {
    label: "Quantity",
    options: quantityOptions,
    default: state.value.selectedQuantityFrequencyCombination.selectedQuantityCode,
  };
});

const priceProps = computed(() => {
  let pricePerUnit: number = selectedQuantity.value.price / parseInt(selectedQuantity.value.code);
  selectedQuantity.value.discount &&
    (pricePerUnit = selectedQuantity.value.discount.priceAfterDiscount / parseInt(selectedQuantity.value.code));

  return {
    originalPrice: selectedQuantity.value.price,
    breakdown: {
      price: pricePerUnit,
      unit: selectedQuantity.value.unit,
    },
    ...(selectedQuantity.value.discount && { discountedPrice: selectedQuantity.value.discount.priceAfterDiscount }),
  };
});

const ctaLinkProps = computed(() => {
  return {
    href: "/consultations/questions/" + buildCode() + "?quantity=" + selectedQuantity.value.code,
    onClick: () => {
      const info = getAnalyticsInfo();

      const boCode: string = buildCode();

      addUserProperties({
        last_selected_product: boCode,
        last_selected_quantity: state.value.selectedQuantityFrequencyCombination.selectedQuantityCode,
        last_selected_frequency:
          state.value.selectedQuantityFrequencyCombination.selectedDeliverySchedule || "ONE_TIME",
      });

      amplitudeEvent("Click Start Consultation", {
        Medicine: info.medicine,
        Treatment: info.treatment,
      });
    },
  };
});

function buildCode(): string {
  let code: string = selectedProduct.value.code;
  switch (selectedProduct.value.actionButtonType) {
    case "DEFAULT":
    case "FIXED_STRENGTH":
      code = code.concat(selectedStrength.value.code);
      break;
    case "FIXED_NO_STRENGTH":
    default:
      break;
  }

  return code;
}

const discountTag = computed(() => {
  const discount = selectedQuantity.value.discount;
  if (discount) {
    if (discount.newCustomerOnly) {
      return {
        renderTick: true,
        discountText: " NEW CUSTOMER DISCOUNT APPLIED",
      };
    } else {
      if (state.value.selectedQuantityFrequencyCombination.selectedDeliverySchedule) {
        return {
          renderTick: false,
          discountText: `${discount.discountSaving} OFF WHEN YOU CHOOSE A SUBSCRIPTION`,
        };
      }

      return {
        renderTick: false,
        discountText: `TAKE ${discount.discountSaving} OFF TODAY WITH PROMO CODE ${discount.discountCode}`,
      };
    }
  }

  return undefined;
});

function handleProductSelected(data: { id: string }): void {
  state.value.selectedProductCode = data.id;
}

function handleStrengthSelected(data: { id: string }): void {
  state.value.selectedStrengthCode = data.id;
}

function handleQuantitySelection(data: { selected: string }): void {
  state.value.selectedQuantityFrequencyCombination.selectedQuantityCode = data.selected;
}

function handleRepeatOrderSelected(data: { id: string }): void {
  state.value.selectedRepeatOrder = data.id;

  switch (state.value.selectedRepeatOrder) {
    case "OTP":
      // Always select first available quantity when switching from SUBSCRIPTION to OTP
      const OTPQuantityFrequencyCombination = {
        selectedQuantityCode: selectedStrength.value.quantities.values().next().value.code,
        selectedDeliverySchedule: undefined,
      };

      state.value.selectedQuantityFrequencyCombination = OTPQuantityFrequencyCombination;
      break;
    case "SUBSCRIPTION":
      // Always select first available quantity when switching from OTP to SUBSCRIPTION
      const firstSubscriptionOption = Array.from(selectedStrength.value.subscriptionFrequencies.values())[0];

      const SubscriptionQuantityFrequencyCombination = {
        selectedQuantityCode: firstSubscriptionOption.quantities.values().next().value.code,
        selectedDeliverySchedule: firstSubscriptionOption.code,
      };

      state.value.selectedQuantityFrequencyCombination = SubscriptionQuantityFrequencyCombination;
      break;
  }
}

function handleDeliveryScheduleSelection(data: { selected: string }): void {
  state.value.selectedQuantityFrequencyCombination = {
    selectedQuantityCode: getAvailableQuantities(data.selected).values().next().value.code,
    selectedDeliverySchedule: data.selected,
  };
}

function getAvailableQuantities(deliverySchedule?: string): Map<String, Quantity> {
  switch (state.value.selectedRepeatOrder) {
    case "OTP":
      return selectedStrength.value.quantities;
    case "SUBSCRIPTION":
      return selectedStrength.value.subscriptionFrequencies.get(deliverySchedule!).quantities;
  }
}

watch(selectedProduct, (newSelectedProduct) => {
  const hasSelectedStrength = !!newSelectedProduct.strengths.get(state.value.selectedStrengthCode);

  if (!hasSelectedStrength) {
    const defaultStrengthOrFirst =
      selectedProduct.value.defaultStrength ||
      Array.from(selectedProduct.value.strengths, ([code, s]) => ({
        code,
        s,
      }))[0].code;

    state.value.selectedStrengthCode = defaultStrengthOrFirst;
  }
});

watch(selectedStrength, (newSelectedStrength) => {
  state.value.selectedQuantityFrequencyCombination.selectedQuantityCode = Array.from(
    newSelectedStrength.quantities,
    ([quantityCode, quantity]) => ({
      quantityCode,
      quantity,
    })
  )[0].quantityCode;

  state.value.selectedRepeatOrder = "OTP";
});

function checkNonNullOrThrow<T>(v: T | undefined, errorMessage: string): T {
  if (!v) {
    throw new Error(errorMessage);
  }

  return v;
}
</script>
<style scoped>
@tailwind utilities;

.item-description-markup >>> * {
  @apply font-normal text-[16px] m-0;
}
</style>
